import { useEffect, useState } from 'react';
import {
  FirearmActionDTO,
  FirearmBarrelDTO,
  FirearmBarrelMakeDTO,
  FirearmCalibreDTO,
  FirearmLooseBarrelTypeDTO,
  FirearmMakeDTO,
  FirearmPurposeDTO,
  FirearmSerialTypeDTO,
  FirearmSpecificDTO,
  FirearmTypeDTO,
  MemberFirearmDTO,
} from '../../services/apiClient';
import FormField from '../common/FormField';
import handleChange from '../../utils/formUtils/handleChange';
import { useFirearmTypes } from '../../hooks/useFirearmTypeApi';
import { useFirearmSpecificsByType, useFirearmSpecificWithPurposes } from '../../hooks/useFirearmSpecificApi';
import { useFirearmLooseBarrelTypes } from '../../hooks/useFirearmLooseBarrelTypeApi';
import { useFirearmMakes } from '../../hooks/useFirearmMakeApi';
import { useFirearmBarrelMakes } from '../../hooks/useFirearmBarrelMakeApi';
import { useFirearmCalibersBySpecific } from '../../hooks/useFirearmCalibreApi';
import { useFirearmActionsBySpecific } from '../../hooks/useFirearmActionApi';
import { useFirearmSerialTypes } from '../../hooks/useFirearmSerialTypeApi';
import { useFirearmBarrelsBySpecific } from '../../hooks/useFirearmBarrelApi';
import { useFirearmPurposes } from '../../hooks/useFirearmPurposeApi';
import { useMemberById } from '../../hooks/useMemberApi';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmOwnerConstants } from '../../constants/FirearmOwnerConstants';
import { FirearmActionConstants } from '../../constants/FirearmActionConstants';
import { FirearmSerialTypeConstants } from '../../constants/FirearmSerialTypeConstants';
import { EndorsementLicenceTypeConstants } from '../../constants/EndorsementLicenceTypeConstants';
import { EndorsementFirearmRenewalConstants } from '../../constants/EndorsementFirearmRenewalConstants';
import { getMemberId, getRoles } from '../../utils/tokenUtils';
import { filterPurposesByMemberCategory } from '../../utils/purposeUtils';

interface FirearmFieldsFormProps {
  formData: MemberFirearmDTO;
  setFormData: (
    data: MemberFirearmDTO | ((prev: MemberFirearmDTO) => MemberFirearmDTO),
  ) => void;
  licenceTypeId?: string;
  memberFirearmId?: string;
  errors: Record<any, any>;
  isLoading?: boolean;
}

const FirearmFieldsForm: React.FC<FirearmFieldsFormProps> = ({
  formData,
  setFormData,
  licenceTypeId,
  memberFirearmId,
  errors,
  isLoading,
}) => {
  const [firearmPurposes, setFirearmPurposes] = useState<FirearmPurposeDTO[]>([]);
  const [selectedFirearmSpecific, setSelectedFirearmSpecific] = useState<FirearmSpecificDTO | null>(null);

  const roles = getRoles() || [];
  const memberId = getMemberId();

  // Lookup tables — globally cached
  const { data: firearmTypesData = [], isLoading: isLoadingFirearmTypes } = useFirearmTypes();
  const { data: firearmMakesData = [], isLoading: isLoadingFirearmMakes } = useFirearmMakes();
  const { data: firearmLooseBarrelTypesData = [] } = useFirearmLooseBarrelTypes();
  const { data: firearmBarrelMakesData = [] } = useFirearmBarrelMakes();
  const { data: firearmSerialTypesData = [], isLoading: isLoadingFirearmSerialTypes } = useFirearmSerialTypes();
  const { data: allPurposesData, isLoading: isLoadingAllPurposes } = useFirearmPurposes();

  // Cascading — cached per selection
  const { data: firearmSpecificsData = [], isLoading: isLoadingFirearmSpecifics } = useFirearmSpecificsByType(formData.firearmTypeId);
  const { data: firearmCalibersData = [], isLoading: isLoadingFirearmCalibers } = useFirearmCalibersBySpecific(formData.firearmSpecificId);
  const { data: firearmActionsData = [], isLoading: isLoadingFirearmActions } = useFirearmActionsBySpecific(formData.firearmSpecificId);
  const { data: firearmBarrelsData = [], isLoading: isLoadingFirearmBarrels } = useFirearmBarrelsBySpecific(formData.firearmSpecificId);

  // FirearmSpecific with its linked purposes (for purpose filtering when a specific is selected)
  const { data: specificWithPurposesData, isLoading: isLoadingSpecificPurposes } = useFirearmSpecificWithPurposes(formData.firearmSpecificId);

  // Member data — only fetched for 'User' role, used for FCA category filtering
  const { data: memberData } = useMemberById(roles.includes('User') ? (memberId ?? undefined) : undefined);

  const isLoadingFirearmPurposes = formData.firearmSpecificId ? isLoadingSpecificPurposes : isLoadingAllPurposes;

  // Derive displayed purpose list — preserves all cascade + FCA filtering logic
  useEffect(() => {
    const memberCatId = memberData?.memberCategoryId;

    if (formData.firearmSpecificId && specificWithPurposesData) {
      setSelectedFirearmSpecific(specificWithPurposesData);

      // Stage 1: Filter purposes to only those linked to this FirearmSpecific
      const linkedPurposes = specificWithPurposesData.purposes || [];

      // Stage 2: Apply FCA filtering for non-admin users
      const filtered = roles.includes('User') && memberCatId
        ? filterPurposesByMemberCategory(linkedPurposes, memberCatId)
        : linkedPurposes;

      setFirearmPurposes(filtered);
    } else if (!formData.firearmSpecificId) {
      // Reset to all purposes when no specific firearm selected
      setSelectedFirearmSpecific(null);

      const all = allPurposesData || [];

      // Apply FCA filtering for non-admin users
      const filtered = roles.includes('User') && memberCatId
        ? filterPurposesByMemberCategory(all, memberCatId)
        : all;

      setFirearmPurposes(filtered);
    }
  }, [formData.firearmSpecificId, specificWithPurposesData, allPurposesData, memberData]); // Re-filter when member data loads

  // Expose typed aliases matching the JSX references
  const firearmTypes = firearmTypesData as FirearmTypeDTO[];
  const firearmMakes = firearmMakesData as FirearmMakeDTO[];
  const firearmLooseBarrelTypes = firearmLooseBarrelTypesData as FirearmLooseBarrelTypeDTO[];
  const firearmBarrelMakes = firearmBarrelMakesData as FirearmBarrelMakeDTO[];
  const firearmSerialTypes = firearmSerialTypesData as FirearmSerialTypeDTO[];
  const firearmSpecifics = firearmSpecificsData as FirearmSpecificDTO[];
  const firearmCalibers = firearmCalibersData as FirearmCalibreDTO[];
  const firearmActions = firearmActionsData as FirearmActionDTO[];
  const firearmBarrels = firearmBarrelsData as FirearmBarrelDTO[];

  const handleMakeOther = () => {
    setFormData((prev) => ({
      ...prev,
      firearmMakeId: FirearmMakeConstants.NOT_ON_LIST,
    }));
  };

  const handleCaliberOther = () => {
    setFormData((prev) => ({
      ...prev,
      firearmCaliberId: FirearmCaliberConstants.NOT_ON_LIST,
    }));
  };

  return (
    <>
      {(formData.firearmOwnerId ||
        licenceTypeId ===
        EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
        memberFirearmId === EndorsementFirearmRenewalConstants.NEW_FIREARM) && (
          <>
            <FormField
              id="firearmTypeId"
              label="Firearm Type"
              type="dropdown"
              value={formData.firearmTypeId || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Select Firearm Type"
              required={
                !!(
                  formData.firearmOwnerId ||
                  licenceTypeId ===
                  EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
                  memberFirearmId ===
                  EndorsementFirearmRenewalConstants.NEW_FIREARM
                )
              }
              error={errors.firearm?.firearmTypeId || errors.firearmTypeId}
              options={firearmTypes.map((type) => ({
                value: type.id,
                label: type.title || '',
              }))}
              isLoading={isLoadingFirearmTypes}
            />
            {formData.firearmTypeId && (
              <>
                <FormField
                  id="firearmMakeId"
                  label="Firearm Make"
                  type="dropdown"
                  value={formData.firearmMakeId || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  placeholder="Select Firearm Make"
                  required={
                    !!(
                      formData.firearmOwnerId ||
                      licenceTypeId ===
                      EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
                      memberFirearmId ===
                      EndorsementFirearmRenewalConstants.NEW_FIREARM
                    )
                  }
                  error={errors.firearm?.firearmMakeId || errors.firearmMakeId}
                  options={firearmMakes.map((type) => ({
                    value: type.id,
                    label: type.title || '',
                  }))}
                  isLoading={isLoadingFirearmMakes}
                  helper={
                    <span>
                      Not on list?{' '}
                      <a
                        className="cursor-pointer text-primary"
                        onClick={handleMakeOther}
                      >
                        Click here
                      </a>
                    </span>
                  }
                />
                {formData.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST && (
                  <FormField
                    id="firearmMakeOther"
                    label="Make Name"
                    type="text"
                    value={formData.firearmMakeOther || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Make Name"
                    required={formData.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST}
                    error={errors.firearmMakeOther}
                  />
                )}
              </>
            )}
            <FormField
              id="firearmSpecificId"
              label="Specific Firearm"
              type="dropdown"
              value={formData.firearmSpecificId || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Select Firearm Specific"
              required={
                !!(
                  formData.firearmOwnerId ||
                  licenceTypeId ===
                  EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
                  memberFirearmId ===
                  EndorsementFirearmRenewalConstants.NEW_FIREARM
                )
              }
              error={
                errors.firearm?.firearmSpecificId || errors.firearmSpecificId
              }
              options={firearmSpecifics.map((type) => ({
                value: type.id,
                label: type.title || '',
              }))}
              isLoading={isLoadingFirearmSpecifics}
            />
            {formData.firearmTypeId &&
              <FormField
                id="firearmCaliberId"
                label="Caliber"
                type="dropdown"
                value={formData.firearmCaliberId || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Select Caliber"
                required={
                  !!(
                    formData.firearmOwnerId ||
                    licenceTypeId ===
                    EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
                    memberFirearmId ===
                    EndorsementFirearmRenewalConstants.NEW_FIREARM
                  )
                }
                error={errors.firearm?.firearmCaliberId || errors.firearmCaliberId}
                options={[
                  ...firearmCalibers.map((type) => ({
                    value: type.id,
                    label: type.title || '',
                  })),
                  ...(firearmCalibers.some((c) => c.id === FirearmCaliberConstants.NOT_ON_LIST)
                    ? []
                    : [{ value: FirearmCaliberConstants.NOT_ON_LIST, label: 'Not on List' }]),
                ]}
                isLoading={isLoadingFirearmCalibers}
                helper={
                  <span>
                    Not on list?{' '}
                    <a
                      className="cursor-pointer text-primary"
                      onClick={handleCaliberOther}
                    >
                      Click here
                    </a>
                  </span>
                }
              />
            }
            {formData.firearmCaliberId ===
              FirearmCaliberConstants.NOT_ON_LIST && (
                <FormField
                  id="caliberOther"
                  label="Caliber Other"
                  type="text"
                  value={formData.caliberOther || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  placeholder="Enter Caliber Other"
                  required={formData.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST}
                  error={errors.caliberOther}
                />
              )}
            {(formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM ||
              licenceTypeId ===
              EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
              memberFirearmId ===
              EndorsementFirearmRenewalConstants.NEW_FIREARM) && (
                <>
                  <FormField
                    id="firearmActionId"
                    label="Firearm Action"
                    type="dropdown"
                    value={formData.firearmActionId || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Select Firearm Action"
                    required={
                      formData.firearmOwnerId ===
                      FirearmOwnerConstants.MY_FIREARM ||
                      licenceTypeId ===
                      EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM ||
                      memberFirearmId ===
                      EndorsementFirearmRenewalConstants.NEW_FIREARM
                    }
                    error={
                      errors.firearm?.firearmActionId || errors.firearmActionId
                    }
                    options={firearmActions.map((type) => ({
                      value: type.id,
                      label: type.title || '',
                    }))}
                    isLoading={isLoadingFirearmActions}
                  />
                  {formData.firearmActionId === FirearmActionConstants.OTHER && (
                    <FormField
                      id="firearmActionOther"
                      label="Firearm Action Other"
                      type="text"
                      value={formData.firearmActionOther || ''}
                      onChange={(e) => handleChange(e, setFormData)}
                      placeholder="Enter Firearm Action Other"
                      required={false}
                      error={errors.firearmActionOther}
                    />
                  )}
                </>
              )}
          </>
        )}

      {(formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM ||
        licenceTypeId) && (
          <>
            <FormField
              id="firearmSerialTypeId"
              label="Type of Serial Number"
              type="dropdown"
              value={formData.firearmSerialTypeId || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Select Firearm Serial Type"
              required={licenceTypeId ? true : false}
              error={
                errors.firearm?.firearmSerialTypeId || errors.firearmSerialTypeId
              }
              options={firearmSerialTypes.map((type) => ({
                value: type.id,
                label: type.title || '',
              }))}
              isLoading={isLoadingFirearmSerialTypes}
            />
            {(formData.firearmSerialTypeId ===
              FirearmSerialTypeConstants.RECEIVER_FRAME_SERIAL_NUMBER ||
              formData.firearmSerialTypeId ===
              FirearmSerialTypeConstants.BARREL_AND_RECEIVER_FRAME_SERIAL_NUMBER) && (
                <FormField
                  id="frameSerial"
                  label="Frame / Receiver Serial Number"
                  type="text"
                  value={formData.frameSerial || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  placeholder="Enter Frame / Receiver Serial Number"
                  required={licenceTypeId ? true : false}
                  error={errors.firearm?.frameSerial || errors.frameSerial}
                />
              )}
            {(formData.firearmSerialTypeId ===
              FirearmSerialTypeConstants.BARREL_SERIAL_NUMBER ||
              formData.firearmSerialTypeId ===
              FirearmSerialTypeConstants.BARREL_AND_RECEIVER_FRAME_SERIAL_NUMBER) && (
                <FormField
                  id="barrelSerial"
                  label="Barrel Serial Number"
                  type="text"
                  value={formData.barrelSerial || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  placeholder="Enter Barrel Serial Number"
                  required={licenceTypeId ? true : false}
                  error={errors.firearm?.barrelSerial || errors.barrelSerial}
                />
              )}
            {(formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM ||
              licenceTypeId ===
              EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM) && (
                <FormField
                  id="firearmBarrelId"
                  label="Barrel configuration"
                  type="dropdown"
                  value={formData.firearmBarrelId || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  placeholder="Select Barrel Configuration"
                  required={
                    formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM ||
                    licenceTypeId ===
                    EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM
                  }
                  error={errors.firearm?.firearmBarrelId || errors.firearmBarrelId}
                  options={firearmBarrels.map((type) => ({
                    value: type.id,
                    label: type.title || '',
                  }))}
                  isLoading={isLoadingFirearmBarrels}
                />
              )}
          </>
        )}

      {licenceTypeId === EndorsementLicenceTypeConstants.LICENCE_FOR_NEW_FIREARM && (
          <FormField
            id="barrelLength"
            label="Barrel Length"
            type="text"
            value={formData.barrelLength || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Barrel Length"
            required={false}
            error={errors.barrelLength}
          />
        )}
      {(formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM ||
        licenceTypeId) && (
          <FormField
            id="firearmPurposeId"
            label="Purpose/use of Firearm"
            type="dropdown"
            value={formData.firearmPurposeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Firearm Purpose"
            required={
              formData.firearmOwnerId === FirearmOwnerConstants.MY_FIREARM ||
                licenceTypeId
                ? true
                : false
            }
            error={errors.firearm?.firearmPurposeId || errors.firearmPurposeId}
            options={firearmPurposes.map((type) => ({
              value: type.id,
              label: type.title || '',
            }))}
            isLoading={isLoadingFirearmPurposes}
          />
        )}
    </>
  );
};

export default FirearmFieldsForm;
